import { useCallback, useEffect, useState } from 'react'

import { getOrganizationDirectoryUsers } from '@/organizations/fetch/get-organization-directory-users'
import type { GetOrganizationDirectoryUsersResponse } from '@/organizations/schemas'

type UseOrganizationDirectoryUsersResult = {
  data: GetOrganizationDirectoryUsersResponse | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => void
}

/**
 * Fetches the member-visible organization directory (display name, email, role) when enabled.
 *
 * Accessible to any org member. Used by the account-menu org roster for non-admins.
 * Returns an RTK-Query-compatible shape so callers read data/isLoading/refetch the same way.
 */
export function useOrganizationDirectoryUsers({ enabled }: { enabled: boolean }): UseOrganizationDirectoryUsersResult {
  const [data, setData] = useState<GetOrganizationDirectoryUsersResponse | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(enabled)
  const [isError, setIsError] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const refetch = useCallback(async () => {
    setIsLoading(true)
    setIsError(false)
    setError(null)
    try {
      setData(await getOrganizationDirectoryUsers())
    } catch (e) {
      setIsError(true)
      setError(e)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return
    }
    void refetch()
  }, [enabled, refetch])

  return { data, isLoading, isError, error, refetch }
}
